import { MultiFormAccordionService } from '@/components/multi-form-accordion/multi-form-accordion.service';
import { ElfBaseStore } from '@/store/base-store.repository';
import { DacDocSubmissionRepositoryFactory } from '@/store/dac-doc-submission/dac-doc-submission-state.repository';
import { DacDocSubmissionStateProps } from '@/store/dac-doc-submission/dac-doc-submission-state.store';
import { DacDocSubmission, DacDocSubmissionDto } from '@/types';
import { form } from '@/utils/form.utils';
import { inject, Injectable, OnDestroy } from '@angular/core';
import { select } from '@ngneat/elf';
import _ from 'lodash';
import { MessageService } from 'primeng/api';
import { Subscription } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class DacSubmissionPiecesService implements OnDestroy {
  multiFormAccordionService = inject(MultiFormAccordionService);

  constructor(multiFormAccordionService?: MultiFormAccordionService) {
    if (multiFormAccordionService) this.multiFormAccordionService = multiFormAccordionService;
  }

  messageService = inject(MessageService);

  private storeSubscriptions: Subscription[] = [];

  initialize() {
    this.subscribeToSubmissionPiecesStores();
    this.storeSubscriptions.push(
      this.multiFormAccordionService.newStore$.subscribe(store => {
        this.subscribeToStore(store);
      })
    );
    this.subscribeToStores();
  }

  subscribeToSubmissionPiecesStores() {
    const stores = this.multiFormAccordionService.getAllStores();
    this.storeSubscriptions = stores.map(store => {
      return store.pipe(select(state => state)).subscribe();
    });
  }

  subscribeToStore(store?: ElfBaseStore) {
    const sub = store?.pipe(select(state => state)).subscribe();
    this.storeSubscriptions.push(sub!);
  }

  subscribeToStores() {
    this.subscribeToSubmissionPiecesStores();
    this.storeSubscriptions.push(
      this.multiFormAccordionService.newStore$.subscribe(store => {
        this.subscribeToStore(store);
      })
    );
    this.storeSubscriptions.push(this.multiFormAccordionService.storeRemoved$.subscribe());
  }

  getAllSubmissionPiecesDtos() {
    return this.multiFormAccordionService.getAllStores().map((store: ElfBaseStore<DacDocSubmissionStateProps>) => {
      const repo = DacDocSubmissionRepositoryFactory.create(store);
      return repo.get<DacDocSubmissionDto>('dto');
    });
  }

  validateSubmissionPiecesDtos(): boolean {
    let isValid = true;

    this.multiFormAccordionService.getAllStores().forEach((store: ElfBaseStore<DacDocSubmissionStateProps>, index) => {
      const repo = DacDocSubmissionRepositoryFactory.create(store);
      // const dto = repo.get<DynamicFieldDto>('dto');

      const result = { success: true, error: {} as any };
      if (!result.success) {
        isValid = false;
        const fieldErrors = result.success ? {} : result.error.flatten().fieldErrors;
        repo.set('errors', fieldErrors);
        MultiFormAccordionService.openPanelIfClosed(`mfa-${index}`);
        form.scrollToInvalidElement();
      } else {
        repo.set('errors', {});
      }
    });

    return isValid;
  }

  setSubmissionPiecesDto(response: DacDocSubmission): void {
    const newStore = this.multiFormAccordionService._formFactory?.(response?.docSoumission.id.toString());
    if (!newStore) {
      console.error(`Could not create store for dynamic field with id: ${response.docSoumission.id}`);
      return;
    }

    const root = this.multiFormAccordionService.root;
    if ('children' in root) {
      root.children.push({ store: newStore });
    }

    this.multiFormAccordionService.newStore$.next(newStore);

    const repo = DacDocSubmissionRepositoryFactory.create(newStore);
    const data = {
      dacId: response.dacId,
      docSoumissionId: response.docSoumission.id,
      dateValidate: new Date(response.dateValidate),
      commentaire: response.commentaire,
    } satisfies DacDocSubmissionDto;
    repo.set('dto', data);
    repo.set('initialDto', structuredClone(data));
    repo.set('folderTypeSelected', { name: response.docSoumission.typeDossier?.libelle, code: response.docSoumission.typeDossier?.id });
    repo.set('docSubmissionNotInDacSelected', { name: response.docSoumission.nom, code: response.docSoumission.id });
  }

  setSubmissionPiecesDtos(responses: DacDocSubmission[]): void {
    responses.forEach(r => {
      this.setSubmissionPiecesDto(r);
    });
  }

  altered(): boolean {
    return this.multiFormAccordionService.getAllStores().some(store => {
      const repo = DacDocSubmissionRepositoryFactory.create(store);
      const current = repo.get<DacDocSubmissionDto>('dto');
      const initial = repo.get<DacDocSubmissionDto>('initialDto');

      if (!initial) return true;

      return !_.isEqual(current, initial);
    });
  }

  clearAttribute(attribute: string) {
    this.multiFormAccordionService.getAllStores().forEach(store => {
      const repo = DacDocSubmissionRepositoryFactory.create(store);
      repo.setNested(attribute, undefined);
    });
  }

  clearNestedDtoAttribute(attribute: string) {
    this.multiFormAccordionService.getAllStores().forEach(store => {
      const repo = DacDocSubmissionRepositoryFactory.create(store);
      repo.setNested('dto', attribute, undefined);
    });
  }

  resetAllDynamicFields() {
    this.multiFormAccordionService.getAllStores().forEach(store => {
      const repo = DacDocSubmissionRepositoryFactory.create(store);
      repo.reset();
    });
  }

  ngOnDestroy() {
    this.storeSubscriptions.forEach(sub => sub.unsubscribe());
  }
}
